import { useMemo, useState } from "react";
import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import useUpdateShopPreferences from "@babackapp/private-api-client/client/use-update-shop-preferences";
import { useViewerHasAtLeastAdminRights } from "@/lib/rights/use-viewer-has-right";
import { PremiumFeature } from "@/components-legacy/premium-feature";
import { SettingsFormHeader } from "@babackapp/react-hook-forms-components/form-page";
import { Card, CardContent } from "@babackapp/ui/components/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@babackapp/ui/components/collapsible";
import { Button } from "@babackapp/ui/components/button";
import { ChevronRight, Loader2 } from "lucide-react";
import { cn } from "@/utils/cn";
import { ExtensionWordingField } from "./extension-wording-field";
import {
  EXTENSION_KEYS,
  SECTION_LABELS,
  type ExtensionSection,
} from "./extension-keys";

type ExtensionTranslations = {
  returnButton?: { label?: Record<string, string> };
  giftCode?: {
    title?: Record<string, string>;
    description?: Record<string, string>;
    linkText?: Record<string, string>;
  };
};

export function ShopifyExtensionsWordingCard() {
  const { data: shop } = useCurrentShopSuspense();
  const hasAdminRight = useViewerHasAtLeastAdminRights();
  const { mutateAsync: updatePreferences, isPending } = useUpdateShopPreferences(shop.id);

  const [expandedSections, setExpandedSections] = useState<
    Set<ExtensionSection>
  >(new Set(["returnButton"]));
  const [localTranslations, setLocalTranslations] =
    useState<ExtensionTranslations>(
      () =>
        (shop.preferences?.extensionTranslations as ExtensionTranslations) ??
        {},
    );
  const [isDirty, setIsDirty] = useState(false);

  const disabled =
    !hasAdminRight || !shop.plan?.customisation
      ? "Vous devez être admin pour modifier ces paramètres"
      : false;

  const languages = useMemo(() => {
    const codes =
      shop.preferences?.languages?.map((lang: string) =>
        lang.toLowerCase().split("-")[0],
      ) ?? [];
    return [...new Set(["en", ...codes])];
  }, [shop.preferences?.languages]);

  const toggleSection = (section: ExtensionSection) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(section)) {
        next.delete(section);
      } else {
        next.add(section);
      }
      return next;
    });
  };

  const handleFieldChange = (path: string, value: Record<string, string>) => {
    const [section, key] = path.split(".") as [ExtensionSection, string];
    setLocalTranslations((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [key]: Object.keys(value).length > 0 ? value : undefined,
      },
    }));
    setIsDirty(true);
  };

  const handleSave = async () => {
    await updatePreferences({
      extensionTranslations: localTranslations,
    });
    setIsDirty(false);
  };

  const groupedKeys = useMemo(() => {
    const bySection = new Map<ExtensionSection, typeof EXTENSION_KEYS>();
    for (const key of EXTENSION_KEYS) {
      const arr = bySection.get(key.section) ?? [];
      arr.push(key);
      bySection.set(key.section, arr);
    }
    return bySection;
  }, []);

  const getValue = (path: string): Record<string, string> | undefined => {
    const [section, key] = path.split(".") as [ExtensionSection, string];
    const sectionData = localTranslations[section];
    if (!sectionData) return undefined;
    return sectionData[key as keyof typeof sectionData] as
      | Record<string, string>
      | undefined;
  };

  return (
    <Card>
      <CardContent className="py-8 flex flex-col gap-6">
        <div className="flex flex-col gap-4">
          <SettingsFormHeader
            title="Extensions Shopify"
            description="Personnalisez les textes des extensions Shopify installées sur votre boutique. Sans personnalisation, la traduction par défaut est utilisée."
          />
          {disabled ? <PremiumFeature /> : null}
        </div>

        <div className="flex flex-col gap-2">
          {(["returnButton", "giftCode"] as ExtensionSection[]).map(
            (section) => {
              const keys = groupedKeys.get(section) ?? [];
              const isOpen = expandedSections.has(section);

              return (
                <Collapsible
                  key={section}
                  open={isOpen}
                  onOpenChange={() => toggleSection(section)}
                >
                  <CollapsibleTrigger className="flex items-center gap-2 w-full text-left px-2 py-2 rounded-md hover:bg-accent transition-colors">
                    <ChevronRight
                      className={cn(
                        "size-4 text-muted-foreground transition-transform shrink-0",
                        isOpen && "rotate-90",
                      )}
                    />
                    <h4 className="text-sm font-semibold">
                      {SECTION_LABELS[section]}
                    </h4>
                    <span className="ml-auto text-xs text-muted-foreground">
                      {keys.length} {keys.length === 1 ? "clé" : "clés"}
                    </span>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="flex flex-col gap-4 mt-2 pl-6">
                      {keys.map((descriptor) => (
                        <ExtensionWordingField
                          key={descriptor.path}
                          descriptor={descriptor}
                          languages={languages}
                          value={getValue(descriptor.path)}
                          onChange={(value) =>
                            handleFieldChange(descriptor.path, value)
                          }
                          disabled={disabled}
                        />
                      ))}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              );
            },
          )}
        </div>

        {isDirty && (
          <div className="flex justify-end">
            <Button onClick={handleSave} disabled={isPending}>
              {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Enregistrer
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
